import { Button, Hairline, Loading, Message, Title } from "@components";

import styles from "styles/pages/reports/reports.module.scss";

import ContemplationXUses from "../Marketing/Coupons/ContemplationXUses";
import UsageBehavior from "../Marketing/Coupons/UsageBehavior";
import UsesAndExpiration from "../Marketing/Coupons/UsesAndExpiration";
import DistribuitionUses from "../Marketing/Coupons/DistribuitionUses";
import CouponsInPeriod from "../Marketing/Coupons/CouponsInPeriod";
import UsageRecord from "./UsageRecord";
import { Currency } from "shared/utils/Currency";
import { If } from "shared/components/control-statements";
import { useEffect, useRef, useState } from "react";
import Filters from "../Filters";
import ReportFilters from "../ReportFilters";
import { ReportsAPI } from "providers/ReportsAPI";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import { useParams, useSearchParams } from "react-router-dom";
import { Numbers } from "shared/utils/Numbers";
import dayjs from "dayjs";

const numbers = (data = {}, isBeforeFebruary) => {
  return [
    {
      title: "Faturamento bruto",
      value: Currency.formatCurrency(data.total),
      tooltip:
        "Soma do valor total bruto de todos os pedidos utilizando cupons.",
    },
    {
      title: "Descontos totais",
      value: isBeforeFebruary ? "---" : Currency.formatCurrency(data.discounts),
      tooltip:
        "Soma do valor total de descontos aplicados nos pedidos que utilizaram cupons.",
    },
    {
      title: "Faturamento líquido",
      value: isBeforeFebruary
        ? "---"
        : Currency.formatCurrency(data.total_with_discounts),
      tooltip:
        "Soma do valor total líquido de todos os pedidos utilizando cupons.",
    },
  ];
};

const numbers2 = (data = {}) => {
  return [
    {
      title: "Quantidade de usos",
      value: Numbers.formatFloat(data.uses),
    },
    {
      title: "Taxa de conversão",
      value: Numbers.formatPercent(data.conversion_rate),
      tooltip: "",
    },
    {
      title: "Taxa de expiração",
      value: Numbers.formatPercent(data.expiration_rate),
      tooltip: "",
    },
  ];
};

const numbers3 = (data = {}, isBeforeFebruary) => {
  return [
    {
      title: "Ticket médio bruto",
      value: Currency.formatCurrency(data.average_ticket),
      tooltip:
        "Soma do valor total bruto dos pedidos que utilizaram este cupom, divididos pela quantidade de pedidos que utilizaram cupons.",
    },
    {
      title: "Ticket médio líquido",
      value: isBeforeFebruary
        ? "---"
        : Currency.formatCurrency(data.average_ticket_with_discounts),
      tooltip:
        "Soma do valor total líquido dos pedidos que utilizaram este cupom, divididos pela quantidade de pedidos que utilizaram cupons.",
    },
    {
      title: "Tempo médio para uso",
      value: `${Numbers.formatFloat(data.time_to_use)} dias`,
      tooltip: "Média do tempo entre as adições e os usos deste cupom.",
    },
    {
      title: "Roi dos cupons",
      value: isBeforeFebruary ? "---" : data.roi || 0,
      tooltip:
        "Retorno sobre o investimento do cupom. FÓRMULA: {{ ((Faturamento gerado - Descontos totais) / Descontos totais) * 100 }}.",
    },
  ];
};

const BigNumber = ({ data }) => {
  return (
    <div className={styles.bigNumber}>
      <div className="d-flex flex-column">
        <div className="d-flex align-center">
          <p className={styles.number}>{data.value}</p>
        </div>
        <div className={styles.title}>
          <p>{data.title}</p>
          <If cond={data.tooltip}>
            <span className={styles.info} title={data.tooltip}>
              <i className="fal fa-info-circle" />
            </span>
          </If>
        </div>
      </div>
    </div>
  );
};

export default function Index() {
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [dateFilters, setDateFilters] = useState({});
  const [data, setData] = useState({
    period: [],
    keynumbers: {},
  });
  const [couponUses, setCouponUses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label_dict, setLabelDict] = useState(new Map());
  const [currentPage, setCurrentPage] = useState(1);
  // const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = couponUses.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = data.total_pages || 1;

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get("campanha");
  const creativeId = searchParams.get("criativo");

  const filtersRef = useRef(null);

  const provider = new ReportsAPI();

  const fetchCampaignAndCreative = async () => {
    const campaign = await provider.flowCampaign(campaignId);
    const creative = await provider.flowCreative(campaignId, creativeId);

    setData((prevState) => ({
      ...prevState,
      campaign: campaign,
      creative: creative,
    }));
  };

  const fetchData = async () => {
    const response = await provider.flowCoupon(
      campaignId,
      creativeId,
      id,
      filters
    );
    setData(response);
  };

  const fetchCouponUses = async () => {
    const provider = new ReportsAPI();
    const response = await provider.flowCouponUses(campaignId, creativeId, id);

    setCouponUses(response.data);
  };

  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true);
      await Promise.all([
        fetchData(),
        fetchCouponUses(),
        fetchUnits(),
        fetchCampaignAndCreative(),
      ]);
      setLoading(false);
    };

    fetchDataAsync();
  }, [filters]);

  const fetchUnits = async () => {
    const response_units = await OlgaEnvironmentsAPI.getUnits();
    const response_brands = await OlgaEnvironmentsAPI.getBrands();

    const units = response_units.getData([]);
    const brands = response_brands.getData([]);

    setLabelDict(
      new Map([
        ...units.map((unit) => [`units_${unit.id}`, unit.name]),
        ...brands.map((brand) => [`brands_${brand.id}`, brand.name]),
      ])
    );
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      // fetchCouponUses(page);
      setCurrentPage(page);
    }
  };

  const september2025 = dayjs("2025-09-01T00:00:00.000Z");
  const currentBeforeSeptember = dayjs(filters?.state_date || "").isBefore(
    september2025,
    "month"
  );

  const isBeforeSeptember = currentBeforeSeptember;

  const specificDay = filters?.period === "SPECIFIC_DAY";

  const february2025 = dayjs("2025-02-01T00:00:00.000Z");

  const currentBeforeFebruary = dayjs(filters?.start_date || "").isBefore(
    february2025
  );

  const isBeforeFebruary =
    currentBeforeFebruary || filters?.period === "FROM_THE_BEGINNING";

  return (
    <>
      <Title
        title={`Cupom: ${data.name}`}
        backTo={{
          url: "/relatorios/marketing/campanhas",
          name: "Relatórios de campanhas",
        }}
      >
        <Filters
          onRemoveFilter={(filter) => {
            filtersRef.current?.removeFilter(filter);
          }}
          filters={filters}
          units={label_dict}
        />
        <Button onClick={() => setOpenFilters(true)}>Filtrar</Button>
      </Title>

      <If cond={loading}>
        <Loading fullContent />
      </If>

      <If cond={isBeforeSeptember}>
        <Message type="warning" className="mb-15">
          Devido a data de criação do cupom, os seguintes dados não serão
          exibidos por falta de registro: Descontos totais e ROI do Cupom.
        </Message>
      </If>

      <If cond={isBeforeFebruary}>
        <Message type="warning" className="mb-15">
          Devido ao período filtrado, alguns dados não serão exibidos.
        </Message>
      </If>

      <div className={styles.bigNumbersIdentification}>
        {numbers(data.keynumbers || {}, isBeforeFebruary).map((item) => (
          <BigNumber key={item.title} data={item} />
        ))}
      </div>
      <div className={styles.bigNumbersIdentification}>
        {numbers2(data.keynumbers || {}, isBeforeFebruary).map((item) => (
          <BigNumber key={item.title} data={item} />
        ))}
      </div>
      <div className={styles.bigNumbersIdentification4}>
        {numbers3(data.keynumbers || {}, isBeforeFebruary).map((item) => (
          <BigNumber key={item.title} data={item} />
        ))}
      </div>

      <If cond={!specificDay}>
        <CouponsInPeriod data={data.period} />
      </If>
      <DistribuitionUses
        data={data.distribution}
        isBeforeSeptember={isBeforeSeptember}
      />
      <ContemplationXUses data={data.gain_uses} />
      <UsageBehavior data={data.behavior} />
      {/* <UsesAndExpiration data={data?.expiration_uses} /> */}
      <UsageRecord
        data={currentItems}
        handlePageChange={handlePageChange}
        currentPage={currentPage}
        totalPages={totalPages}
      />

      <div className={styles.selectedCampaign}>
        <p className={styles.name}>
          Cupom: {data.name} / Criativo: {data?.creative?.name} / Campanha:{" "}
          {data?.campaign?.name}
        </p>
      </div>

      <ReportFilters
        ref={filtersRef}
        open={openFilters}
        dateFiltersProp={dateFilters}
        onClose={() => setOpenFilters(false)}
        onUpdateFilters={(filters) => {
          setFilters(filters);
        }}
        onUpdateDateFilters={(filters) => {
          setDateFilters(filters);
        }}
      />
    </>
  );
}
